import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const reviews = [
  {
    name: 'Alicia',
    text: "Perfect! Hamza explains complex chemistry concepts with clarity, and consistently checks Alicia's understanding. He is highly methodical, setting clear learning goals that help Alicia focus on what matters most. Hamza also uses effective strategies to assess her progress from one lesson to the next. He is able to adjust his pace and tone to match Alicia's communication style, which fosters a strong sense of trust and rapport. Beyond subject teaching, Hamza shows genuine interest in Alicia's wider school activities and acts as a mentor, guiding her toward effective study habits and a confident approach to learning.",
  },
  {
    name: 'Ishan',
    text: "As a combined Science student, I thought I would struggle keeping up with triple science students in Year 12. But Hamza has really helped me consolidate my knowledge in all topics. He has also provided me with exceptional resources that follow the exam board. I'm now moving on to my final year of A-levels and will without a doubt continue his lessons, to excel and obtain the best grades. Strongly recommend him!",
  },
  {
    name: 'Indy',
    text: 'Perfect! My son has found Hamza to be friendly and easy to talk to. He has very good teaching techniques which has helped to make understanding the information easier.',
  },
  {
    name: 'Sami',
    text: "Perfect! Just wanted to say that my daughter Sami has had Hamza as a tutor for just a short period of time but she has only had good things to say about him after the lesson ends as he explains topics thoroughly going through the theory of things and not just saying it is this because it's for her exam board. He is also great as he is available to explain things all day long and again gives explanations on exam questions if she is stuck on something — honestly thank you so much.",
  },
  {
    name: 'Mohammed Amir',
    text: "Effective, organised and efficient! Within the span of a few months, Hamza has consolidated my son's knowledge and most importantly built confidence in him to achieve great results. He is punctual, polite and diligent, providing great feedback. My son is looking forward to continue his journey in Sciences with Hamza.",
  },
  {
    name: 'Yaseen',
    text: "Strongly recommend Hamza! There was a significant jump between GCSE and Year 12, and I really struggled for the first few months, until Hamza taught me 2 hrs a week for a few months. It's helped me get back into the game and achieve grade A in my Year 12 exams in Chemistry. Very relieved! I would without a doubt continue for my Year 13 exams, but this time, do both Chemistry AND Biology.",
  },
];

const BehindResults = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 6000);
    return () => clearInterval(t);
  }, []);

  const review = reviews[idx];

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="font-display text-[36px] md:text-[48px] tracking-tight font-semibold text-neutral-900"
        >
          Behind the results
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-4 text-[14px] md:text-[15px] text-neutral-600 max-w-xl mx-auto"
        >
          Hear directly from students and parents about their experience.
        </motion.p>

        <div
          className="mt-12 max-w-3xl mx-auto"
        >
          <div className="relative min-h-[320px] md:min-h-[280px]">
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={false}
                animate={{ opacity: i === idx ? 1 : 0 }}
                transition={{ duration: 0.9, ease: 'easeInOut' }}
                style={{ pointerEvents: i === idx ? 'auto' : 'none' }}
                className="absolute inset-0 bg-neutral-100 rounded-2xl p-8 md:p-10 text-left"
              >
                <div className="font-display text-[18px] md:text-[20px] font-semibold text-neutral-900">
                  {r.name}
                </div>
                <p className="mt-4 text-[15px] md:text-[16px] leading-[1.75] text-neutral-700">
                  {r.text}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              onClick={() => setIdx((idx - 1 + reviews.length) % reviews.length)}
              className="w-9 h-9 rounded-full border border-neutral-300 hover:bg-neutral-100 flex items-center justify-center transition-colors"
              aria-label="Previous review"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-6 bg-neutral-900' : 'w-2 bg-neutral-300'}`}
                  aria-label={`Go to review ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={() => setIdx((idx + 1) % reviews.length)}
              className="w-9 h-9 rounded-full border border-neutral-300 hover:bg-neutral-100 flex items-center justify-center transition-colors"
              aria-label="Next review"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BehindResults;
